import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@mui/material';
import { useCreateUserMutation } from '../../hooks/api';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  loading,
  setLoading,
}) => {
  const [newUser, setNewUser] = useState({
    email: '',
    temporaryPassword: '',
  });

  const [formErrors, setFormErrors] = useState({
    email: false,
    temporaryPassword: false,
  });

  const createUserMutation = useCreateUserMutation();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors(prev => ({
      ...prev,
      [name]: value.trim() === '',
    }));
  };

  const validateForm = (): boolean => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email);
    const passwordValid = newUser.temporaryPassword.trim().length >= 8;

    setFormErrors({
      email: !emailValid,
      temporaryPassword: !passwordValid,
    });

    return emailValid && passwordValid;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await createUserMutation.mutateAsync({
        email: newUser.email,
        username: newUser.email,
        temporaryPassword: newUser.temporaryPassword,
      });

      onSuccess();
      handleClose();
    } catch (err) {
      console.error('Error creating user:', err);
      onError(`Failed to create user: ${(err as Error).message ?? 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewUser({
      email: '',
      temporaryPassword: '',
    });
    setFormErrors({
      email: false,
      temporaryPassword: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Create New User</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            value={newUser.email}
            onChange={handleInputChange}
            error={formErrors.email}
            helperText={
              formErrors.email ? 'Valid email is required' : 'Email will be used as username'
            }
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Temporary Password"
            type="password"
            name="temporaryPassword"
            value={newUser.temporaryPassword}
            onChange={handleInputChange}
            error={formErrors.temporaryPassword}
            helperText={
              formErrors.temporaryPassword ? 'Password must be at least 8 characters' : ''
            }
            margin="normal"
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleCreateUser} variant="contained" color="primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
